import { WeightTrajectoryEditor } from './WeightTrajectoryEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldLabel } from '@/components/ui/field';
import type { ClinicalReferences, PrenatalAlertId } from './clinicalReferences';
import preset from './referencePreset.json';
const alerts: [PrenatalAlertId, string][] = [
  ['R-PN-01', 'Elevated blood pressure'],
  ['R-PN-02', 'Severely elevated blood pressure'],
  ['R-PN-03', 'Urine protein 2+ or greater'],
  ['R-PN-04', 'Fundal height discrepancy'],
  ['R-PN-05', 'Weight gain outside reviewed weekly trajectory'],
  ['R-PN-06', 'EDD missing after documented visit'],
  ['R-PN-07', 'Postdates'],
  ['R-PN-08', 'Rh-negative: RhIG documentation due (unless declined)'],
  ['R-PN-09', 'GBS status unknown at 37 weeks'],
  ['R-PN-10', 'Fetal heart rate outside reviewed range'],
];
export function ClinicalReferenceEditor({
  value,
  enabledAlerts,
  onChange,
}: {
  value?: ClinicalReferences | null;
  enabledAlerts: PrenatalAlertId[];
  onChange: (value: ClinicalReferences, alerts: PrenatalAlertId[]) => void;
}) {
  function numeric(group: keyof ClinicalReferences, labels: Record<string, string>) {
    const source = value![group] as unknown as Record<string, number>;
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {Object.entries(labels).map(([key, label]) => (
          <Field key={key}>
            <FieldLabel htmlFor={`ref-${group}-${key}`}>{label}</FieldLabel>
            <Input
              id={`ref-${group}-${key}`}
              type="number"
              step="any"
              required
              value={Number.isFinite(source[key]) ? source[key] : ''}
              onChange={(e) =>
                onChange(
                  {
                    ...value!,
                    [group]: { ...source, [key]: e.target.value === '' ? Number.NaN : Number(e.target.value) },
                  },
                  enabledAlerts,
                )
              }
            />
          </Field>
        ))}
      </div>
    );
  }
  return (
    <details>
      <summary>Clinical references and prenatal alerts</summary>
      <div className="flex flex-col gap-5 pt-4">
        <p>
          Reference values below require practice review. Loading proposed values does not activate them. Weight-gain
          ranges describe total gain; no unreviewed weekly trajectory is inferred.
        </p>
        {!value ? (
          <Button type="button" variant="outline" onClick={() => onChange(structuredClone(preset), [])}>
            Load proposed references for review
          </Button>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <Checkbox
                id="references-enabled"
                checked={value.enabled}
                onCheckedChange={(v) => onChange({ ...value, enabled: v === true }, v === true ? enabledAlerts : [])}
              />
              <label htmlFor="references-enabled">
                Activate these references when this configuration is published after review
              </label>
            </div>
            <Field>
              <FieldLabel htmlFor="reference-citation">Reference sources and practice rationale</FieldLabel>
              <Textarea
                id="reference-citation"
                required
                value={value.citation}
                maxLength={2000}
                onChange={(e) => onChange({ ...value, citation: e.target.value }, enabledAlerts)}
              />
            </Field>
            <WeightTrajectoryEditor
              value={value.weight_gain_trajectories ?? []}
              onChange={(trajectories) => onChange({ ...value, weight_gain_trajectories: trajectories }, enabledAlerts)}
            />
            <h4>BMI input plausibility limits</h4>
            {numeric('bmi_limits', {
              minHeightCm: 'Minimum height (cm)',
              maxHeightCm: 'Maximum height (cm)',
              minWeightKg: 'Minimum weight (kg)',
              maxWeightKg: 'Maximum weight (kg)',
            })}
            {(['singleton_gain_bands', 'twin_gain_bands'] as const).map((group) => (
              <section key={group} className="flex flex-col gap-3">
                <h4>{group === 'singleton_gain_bands' ? 'Singleton total gain bands' : 'Twin total gain bands'}</h4>
                {value[group].map((band, index) => (
                  <div key={index} className="grid gap-3 rounded border p-3 sm:grid-cols-2">
                    {(['minBmi', 'maxBmi', 'lowLb', 'highLb'] as const).map((key) => (
                      <Field key={key}>
                        <FieldLabel htmlFor={`${group}-${index}-${key}`}>
                          {
                            {
                              minBmi: 'BMI from (inclusive)',
                              maxBmi: 'BMI below (blank = no upper bound)',
                              lowLb: 'Lower total gain (lb)',
                              highLb: 'Upper total gain (lb)',
                            }[key]
                          }
                        </FieldLabel>
                        <Input
                          id={`${group}-${index}-${key}`}
                          type="number"
                          min={0}
                          step="any"
                          required={key !== 'maxBmi'}
                          value={band[key] === null || !Number.isFinite(band[key]) ? '' : band[key]!}
                          onChange={(e) =>
                            onChange(
                              {
                                ...value,
                                [group]: value[group].map((item, i) =>
                                  i === index
                                    ? {
                                        ...item,
                                        [key]:
                                          e.target.value === ''
                                            ? key === 'maxBmi'
                                              ? null
                                              : Number.NaN
                                            : Number(e.target.value),
                                      }
                                    : item,
                                ),
                              },
                              enabledAlerts,
                            )
                          }
                        />
                      </Field>
                    ))}
                  </div>
                ))}
              </section>
            ))}
            <section className="flex flex-col gap-3">
              <h4>Ultrasound redating thresholds</h4>
              <p>
                The discrepancy must be greater than the threshold. GA uses the previously recorded EDD at the scan
                date. A separate Clinical method permits a documented override.
              </p>
              {value.redating_bands.map((band, index) => (
                <div key={index} className="grid gap-3 sm:grid-cols-2">
                  {(['maxDays', 'differenceDays'] as const).map((key) => (
                    <Field key={key}>
                      <FieldLabel htmlFor={`dating-${index}-${key}`}>
                        {key === 'maxDays' ? 'GA through (days)' : 'Discrepancy threshold (days)'}
                      </FieldLabel>
                      <Input
                        id={`dating-${index}-${key}`}
                        type="number"
                        min={0}
                        required
                        value={Number.isFinite(band[key]) ? band[key] : ''}
                        onChange={(e) =>
                          onChange(
                            {
                              ...value,
                              redating_bands: value.redating_bands.map((item, i) =>
                                i === index
                                  ? { ...item, [key]: e.target.value === '' ? Number.NaN : Number(e.target.value) }
                                  : item,
                              ),
                            },
                            enabledAlerts,
                          )
                        }
                      />
                    </Field>
                  ))}
                </div>
              ))}
            </section>
            <h4>Fundal height reference</h4>
            {numeric('fh_window', {
              minDays: 'Window starts (GA days)',
              maxDays: 'Window ends (GA days)',
              toleranceCm: 'Discrepancy tolerance (cm)',
            })}
            <h4>Blood pressure references</h4>
            {numeric('bp_reference', {
              warnSystolic: 'Warning systolic (mmHg)',
              warnDiastolic: 'Warning diastolic (mmHg)',
              criticalSystolic: 'Critical systolic (mmHg)',
              criticalDiastolic: 'Critical diastolic (mmHg)',
            })}
            <h4>Fetal heart rate reference</h4>
            {numeric('fetal_heart_range', { minBpm: 'Lower rate (beats/min)', maxBpm: 'Upper rate (beats/min)' })}
            <h4>Postdates reference</h4>
            {numeric('postdates', { warnDays: 'Warning at GA days', criticalDays: 'Critical at GA days' })}
            <section className="flex flex-col gap-3">
              <h4>Activate individual prenatal alerts</h4>
              {alerts.map(([id, label]) => (
                <div key={id} className="flex items-center gap-3">
                  <Checkbox
                    id={`alert-${id}`}
                    disabled={!value.enabled}
                    checked={enabledAlerts.includes(id)}
                    onCheckedChange={(checked) =>
                      onChange(value, checked ? [...enabledAlerts, id] : enabledAlerts.filter((v) => v !== id))
                    }
                  />
                  <label htmlFor={`alert-${id}`}>{label}</label>
                </div>
              ))}
            </section>
          </>
        )}
      </div>
    </details>
  );
}
